import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import DashboardSkeleton from "../components/dashboard/DashboardSkeleton";
import LoadingTransition from "../components/ui/LoadingTransition";
import Skeleton from "../components/ui/Skeleton";
import {
  Users,
  CreditCard,
  BarChart,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Clock,
  Calendar,
} from "lucide-react";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Stat card component
function StatCard({ title, value, icon, change, changeType, description }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <h3 className="text-2xl font-bold mt-1">{value}</h3>

          {change && (
            <div className="flex items-center mt-2">
              {changeType === "increase" ? (
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
              )}
              <span
                className={
                  changeType === "increase"
                    ? "text-sm font-medium text-green-600"
                    : "text-sm font-medium text-red-600"
                }
              >
                {change}
              </span>
            </div>
          )}

          {description && (
            <p className="text-sm text-gray-500 mt-1">{description}</p>
          )}
        </div>

        <div className="bg-primary-50 p-3 rounded-full">{icon}</div>
      </div>
    </div>
  );
}

// Activity Item component
function ActivityItem({ activity }) {
  return (
    <div className="flex py-3">
      <div className="flex-shrink-0 mr-3">
        <div className="h-8 w-8 rounded-full bg-primary-50 flex items-center justify-center">
          {activity.icon}
        </div>
      </div>
      <div className="flex-1 border-b pb-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">{activity.title}</p>
          <span className="text-xs text-gray-500 flex items-center">
            <Clock className="h-3 w-3 mr-1" />
            {activity.time}
          </span>
        </div>
        <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
      </div>
    </div>
  );
}

// Revenue Chart component
function RevenueChart() {
  const data = {
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    datasets: [
      {
        label: "Monthly Revenue",
        data: [
          12500, 15000, 17800, 16400, 19100, 21500, 24000, 25600, 23400, 22000,
          26700, 27800,
        ],
        backgroundColor: "rgba(99, 102, 241, 0.6)",
        borderColor: "rgba(99, 102, 241, 1)",
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          drawBorder: false,
        },
        ticks: {
          callback: (value) => `$${value / 1000}k`,
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
      },
    },
  };

  return (
    <div className="h-64">
      <Bar data={data} options={options} />
    </div>
  );
}

// User Growth Chart component
function UserGrowthChart() {
  const data = {
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    datasets: [
      {
        label: "New Users",
        data: [450, 550, 620, 590, 730, 810, 880, 950, 920, 1050, 1150, 1250],
        fill: false,
        borderColor: "rgba(99, 102, 241, 1)",
        tension: 0.4,
      },
      {
        label: "Active Users",
        data: [
          850, 940, 1050, 1100, 1250, 1380, 1500, 1620, 1700, 1850, 1970, 2100,
        ],
        fill: false,
        borderColor: "rgba(16, 185, 129, 1)",
        tension: 0.4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: {
            size: 12,
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          drawBorder: false,
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
      },
    },
  };

  return (
    <div className="h-64">
      <Line data={data} options={options} />
    </div>
  );
}

// User Type Chart component
function UserTypeChart() {
  const data = {
    labels: ["Free Users", "Premium Users"],
    datasets: [
      {
        data: [1650, 850],
        backgroundColor: [
          "rgba(209, 213, 219, 0.7)",
          "rgba(99, 102, 241, 0.7)",
        ],
        borderColor: ["rgba(209, 213, 219, 1)", "rgba(99, 102, 241, 1)"],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          font: {
            size: 12,
          },
        },
      },
    },
    cutout: "70%",
  };

  return (
    <div className="h-64 flex items-center justify-center">
      <div className="w-48">
        <Doughnut data={data} options={options} />
      </div>
    </div>
  );
}

// Main Dashboard component
function Dashboard() {
  const { logout } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [isRevenueChartLoading, setIsRevenueChartLoading] = useState(true);
  const [isActivityLoading, setIsActivityLoading] = useState(true);
  const [isUserGrowthLoading, setIsUserGrowthLoading] = useState(true);
  const [isUserTypesLoading, setIsUserTypesLoading] = useState(true);

  useEffect(() => {
    // Simulate initial data loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 6000);

    // Simulate different data sections loading with staggered timing
    const statsTimer = setTimeout(() => setIsStatsLoading(false), 1200);
    const revenueTimer = setTimeout(
      () => setIsRevenueChartLoading(false),
      1600
    );
    const activityTimer = setTimeout(() => setIsActivityLoading(false), 1400);
    const userGrowthTimer = setTimeout(
      () => setIsUserGrowthLoading(false),
      1800
    );
    const userTypesTimer = setTimeout(() => setIsUserTypesLoading(false), 2000);

    return () => {
      clearTimeout(timer);
      clearTimeout(statsTimer);
      clearTimeout(revenueTimer);
      clearTimeout(activityTimer);
      clearTimeout(userGrowthTimer);
      clearTimeout(userTypesTimer);
    };
  }, []);

  // Recent activity data
  const recentActivity = [
    {
      id: 1,
      title: "New Premium Subscription",
      description: "User John Doe upgraded to Premium plan",
      time: "15 minutes ago",
      icon: <CreditCard className="h-4 w-4 text-primary" />,
    },
    {
      id: 2,
      title: "New User Registration",
      description: "Amy Johnson created a new account",
      time: "1 hour ago",
      icon: <Users className="h-4 w-4 text-green-500" />,
    },
    {
      id: 3,
      title: "Voice Command Added",
      description: 'New command "Play sleep music" was added',
      time: "3 hours ago",
      icon: <BarChart className="h-4 w-4 text-blue-500" />,
    },
    {
      id: 4,
      title: "Pet Store Purchase",
      description: "User Mark Wilson purchased new pet accessories",
      time: "5 hours ago",
      icon: <CreditCard className="h-4 w-4 text-purple-500" />,
    },
    {
      id: 5,
      title: "System Update",
      description: "System successfully updated to version 2.4.0",
      time: "1 day ago",
      icon: <BarChart className="h-4 w-4 text-yellow-500" />,
    },
  ];

  return (
    <div className="">
      <main className="">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-gray-600">
            Welcome back, Admin! Here's what's happening.
          </p>
        </div>

        {/* Use LoadingTransition for the entire dashboard content */}
        <LoadingTransition
          isLoading={isLoading}
          skeleton={<DashboardSkeleton />}
          transition={{ duration: 1 }}
        >
          <div>
            {/* Stats Grid */}
            <LoadingTransition
              isLoading={isStatsLoading}
              skeleton={
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index}>
                      <Skeleton.Card />
                    </div>
                  ))}
                </div>
              }
              transition={{ duration: 0.3 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <StatCard
                  title="Total Users"
                  value="2,500"
                  icon={<Users className="h-6 w-6 text-primary" />}
                  change="+15.3%"
                  changeType="increase"
                  description="vs. last month"
                />

                <StatCard
                  title="Premium Users"
                  value="850"
                  icon={<Users className="h-6 w-6 text-green-500" />}
                  change="+12.1%"
                  changeType="increase"
                />

                <StatCard
                  title="Monthly Revenue"
                  value="$27,800"
                  icon={<CreditCard className="h-6 w-6 text-blue-500" />}
                  change="+8.5%"
                  changeType="increase"
                />

                <StatCard
                  title="Conversion Rate"
                  value="34%"
                  icon={<BarChart className="h-6 w-6 text-purple-500" />}
                  change="-2.4%"
                  changeType="decrease"
                  description="vs. last month"
                />
              </div>
            </LoadingTransition>

            {/* Charts & Activity Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Revenue Chart */}
              <div className="lg:col-span-2 bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium">Revenue Overview</h2>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">This Year</span>
                    <div className="flex items-center space-x-1 bg-gray-100 rounded-md px-2 py-1">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium">2023</span>
                    </div>
                  </div>
                </div>

                <LoadingTransition
                  isLoading={isRevenueChartLoading}
                  skeleton={<DashboardSkeleton.Chart />}
                  transition={{ duration: 0.5 }}
                >
                  <RevenueChart />
                </LoadingTransition>
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium">Recent Activity</h2>
                  <button className="text-primary text-sm font-medium flex items-center hover:text-primary-700">
                    <span>View All</span>
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </button>
                </div>

                <LoadingTransition
                  isLoading={isActivityLoading}
                  skeleton={
                    <div className="space-y-3">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <div key={index} className="flex py-3">
                          <Skeleton.Circle size="2rem" className="mr-3" />
                          <div className="flex-1 border-b pb-3">
                            <Skeleton
                              width="60%"
                              height="1rem"
                              className="mb-1"
                            />
                            <Skeleton width="80%" height="0.875rem" />
                          </div>
                        </div>
                      ))}
                    </div>
                  }
                  transition={{ duration: 0.3 }}
                >
                  <div className="space-y-0">
                    {recentActivity.map((activity) => (
                      <ActivityItem key={activity.id} activity={activity} />
                    ))}
                  </div>
                </LoadingTransition>
              </div>
            </div>

            {/* Additional Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* User Growth Chart */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-medium mb-4">User Growth</h2>

                <LoadingTransition
                  isLoading={isUserGrowthLoading}
                  skeleton={<DashboardSkeleton.Chart height="250px" />}
                  transition={{ duration: 0.5 }}
                >
                  <UserGrowthChart />
                </LoadingTransition>
              </div>

              {/* User Types Chart */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-medium mb-4">User Types</h2>

                <LoadingTransition
                  isLoading={isUserTypesLoading}
                  skeleton={<DashboardSkeleton.Chart height="200px" />}
                  transition={{ duration: 0.5 }}
                >
                  <UserTypeChart />

                  <div className="flex justify-around mt-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold">66%</div>
                      <div className="text-sm text-gray-500">Free Users</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">34%</div>
                      <div className="text-sm text-gray-500">Premium Users</div>
                    </div>
                  </div>
                </LoadingTransition>
              </div>
            </div>
          </div>
        </LoadingTransition>
      </main>
    </div>
  );
}

export default Dashboard;
